import React from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

const LANGUAGE_COOKIE = "lang";
const LANGUAGE_OPTIONS = [
  { value: "en", labelKey: "languageSwitcher.english" },
  { value: "vi", labelKey: "languageSwitcher.vietnamese" },
] as const;

type LanguageCode = (typeof LANGUAGE_OPTIONS)[number]["value"];

const normalizeLanguage = (lng?: string): LanguageCode => {
  if (!lng) return "en";
  return lng.toLowerCase().startsWith("vi") ? "vi" : "en";
};

const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const currentLanguage = normalizeLanguage(i18n.language);

  const setLanguage = (nextLanguage: LanguageCode) => {
    if (nextLanguage === currentLanguage) return;
    i18n.changeLanguage(nextLanguage);
    Cookies.set(LANGUAGE_COOKIE, nextLanguage, { expires: 365 });
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (index + direction + LANGUAGE_OPTIONS.length) %
      LANGUAGE_OPTIONS.length;
    const { value } = LANGUAGE_OPTIONS[nextIndex];
    const buttons =
      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
        ".language-switcher__option",
      );
    buttons?.[nextIndex]?.focus();
    setLanguage(value);
  };

  return (
    <div
      className="language-switcher"
      role="group"
      aria-label={t("languageSwitcher.label")}
    >
      <div className="language-switcher__pill">
        {LANGUAGE_OPTIONS.map((option, idx) => {
          const isActive = option.value === currentLanguage;
          return (
            <button
              key={option.value}
              type="button"
              className={`language-switcher__option${isActive ? " language-switcher__option--active" : ""}`}
              onClick={() => setLanguage(option.value)}
              aria-pressed={isActive}
              aria-label={t(option.labelKey)}
              title={t(option.labelKey)}
              onKeyDown={(event) => handleKeyDown(event, idx)}
            >
              {t(option.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSwitcher;

